import { useState, useEffect } from 'react'
import { Form, Button, Alert, ProgressBar, Modal } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'
import { useTaskRuntime } from '../contexts/TaskRuntimeContext'

const DEFAULT_BROWSER_REQUEST_INTERVAL_MS = 350

const normalizeRequestInterval = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.floor(parsed)
    : DEFAULT_BROWSER_REQUEST_INTERVAL_MS
}

// 横向标签列表组件
const HorizontalTagList = ({ items, maxVisible = 5, renderItem, className = '' }) => {
  const [expanded, setExpanded] = useState(false)
  
  const visibleItems = expanded ? items : items.slice(0, maxVisible)
  const hiddenCount = items.length - maxVisible
  
  return (
    <div className={`d-flex flex-wrap align-items-center gap-1 ${className}`}>
      {visibleItems.map((item, index) => renderItem(item, index))}
      
      {hiddenCount > 0 && !expanded && (
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center"
          onClick={() => setExpanded(true)}
          style={{ fontSize: '0.75rem', padding: '2px 6px' }}
        >
          +{hiddenCount} <span className="ms-1">▼</span>
        </button>
      )}
      
      {expanded && hiddenCount > 0 && (
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center"
          onClick={() => setExpanded(false)}
          style={{ fontSize: '0.75rem', padding: '2px 6px' }}
        >
          收起 <span className="ms-1">▲</span>
        </button>
      )}
    </div>
  )
}

const ExecutionController = ({
  selectedTasks,
  selectedAccounts,
  taskConfigs,
  executionMode,
  scheduleTime,
  requestInterval = DEFAULT_BROWSER_REQUEST_INTERVAL_MS,
  activitySkipEnabled,
  onActivitySkipEnabledChange,
  dailyRuntimeResetEnabled,
  onDailyRuntimeResetEnabledChange,
  isExecuting,
  onExecutionModeChange,
  onScheduleTimeChange,
  onRequestIntervalChange,
  onExecute,
  taskManager,
  addLog,
  onLoadJobConfig, // 新增：加载定时任务配置的回调
}) => {
  const [executionProgress, setExecutionProgress] = useState(0)
  const [currentTask, setCurrentTask] = useState('')
  const [currentAccount, setCurrentAccount] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [currentExecutionId, setCurrentExecutionId] = useState(null)
  const [showSaveGroupModal, setShowSaveGroupModal] = useState(false)
  const [selectedJobForGroup, setSelectedJobForGroup] = useState(null)
  const [groupName, setGroupName] = useState('')
  const [groupType, setGroupType] = useState('task') // 'task' | 'user' | 'both'

  const { addTaskGroup, addUserGroup } = useAppStore()

  // 全局运行时负责定时任务，切换页面后仍会保持。
  const {
    scheduledJobsList,
    createScheduledJob,
    cancelScheduledJob
  } = useTaskRuntime()
  const effectiveScheduledJobsList = scheduledJobsList

  const { transportPaused } = useTaskRuntime()
  const canExecute = selectedTasks.length > 0 && selectedAccounts.length > 0 && !isExecuting && !transportPaused

  // 监听队列执行状态
  useEffect(() => {
    if (!taskManager) return

    const handleExecutionComplete = (data) => {
      // 检查是否是当前的执行ID
      if (data.executionId === currentExecutionId) {
        // 执行完成，重置状态
        onExecute(false)
        setExecutionProgress(0)
        setCurrentTask('')
        setCurrentAccount('')
        setCurrentExecutionId(null)
        addLog('任务执行完成', 'success', { executionId: data.executionId })
      }
    }

    const handleExecutionStart = (data) => {
      // 如果是当前的执行ID，更新状态
      if (data.executionId === currentExecutionId) {
        onExecute(true)
        addLog(`开始执行批量任务，执行ID: ${data.executionId}`, 'info')
      }
    }

    // 添加事件监听器
    taskManager.addEventListener('executionComplete', handleExecutionComplete)
    taskManager.addEventListener('executionStart', handleExecutionStart)
    const handlePaused = () => {
      onExecute(false)
      // 保留本批次身份和进度，自动续跑后仍能接收完成事件。
    }
    taskManager.addEventListener('executionPaused', handlePaused)
    
    return () => {
      // 清理事件监听器
      taskManager.removeEventListener('executionComplete', handleExecutionComplete)
      taskManager.removeEventListener('executionStart', handleExecutionStart)
      taskManager.removeEventListener('executionPaused', handlePaused)
    }
  }, [taskManager, currentExecutionId, onExecute, addLog])

  const handleExecuteClick = () => {
    if (executionMode === 'scheduled') {
      setShowConfirmModal(true)
    } else {
      executeOnce()
    }
  }

  const executeOnce = async () => {
    if (!canExecute) return

    // 检查是否已有任务在执行
    if (taskManager?.isExecuting()) {
      addLog('已有任务在执行中，请等待当前任务完成', 'warning')
      return
    }

    onExecute(true)
    setExecutionProgress(0)
    setCurrentTask('')
    setCurrentAccount('')

    // 创建配置快照，避免执行过程中配置被修改
    const configSnapshot = JSON.parse(JSON.stringify(taskConfigs))
    const tasksSnapshot = [...selectedTasks]
    const accountsSnapshot = [...selectedAccounts]
    const requestIntervalSnapshot = normalizeRequestInterval(requestInterval)

    try {
      // 生成唯一的执行ID
      const executionId = `immediate_${Date.now()}`
      setCurrentExecutionId(executionId)

      // 将批量任务添加到队列
      taskManager?.addBatchToQueue({
        source: 'manual',
        tasks: tasksSnapshot,
        accounts: accountsSnapshot,
        configs: configSnapshot,
        requestInterval: requestIntervalSnapshot,
        executionId: executionId,
        onProgress: (progress) => {
          setExecutionProgress(progress)
        },
        onLog: addLog
      })

      addLog(`立即执行任务已加入队列，执行ID: ${executionId}`, 'info')
      
    } catch (error) {
      addLog(`添加任务到队列失败: ${error.message}`, 'error')
      onExecute(false)
      setExecutionProgress(0)
      setCurrentTask('')
      setCurrentAccount('')
      setCurrentExecutionId(null)
    }
  }

  const scheduleExecution = () => {
    // 创建深拷贝的配置快照，确保定时任务的配置独立
    const jobData = {
      tasks: [...selectedTasks],
      accounts: [...selectedAccounts],
      configs: JSON.parse(JSON.stringify(taskConfigs)), // 深拷贝配置
      requestInterval: normalizeRequestInterval(requestInterval),
    }

    // 使用 Hook 创建定时任务
    createScheduledJob(jobData, scheduleTime)
    setShowConfirmModal(false)
  }

  // 保存定时任务为组
  const handleSaveAsGroup = (job) => {
    setSelectedJobForGroup(job)
    setGroupName(`定时任务_${new Date(job.scheduledTime).toLocaleString()}`)
    setGroupType('both')
    setShowSaveGroupModal(true)
  }

  const confirmSaveAsGroup = () => {
    if (!selectedJobForGroup || !groupName.trim()) {
      addLog('请输入组名称', 'warning')
      return
    }

    try {
      // 保存任务组
      if (groupType === 'task' || groupType === 'both') {
        const taskGroupData = {
          name: groupType === 'both' ? `${groupName}_任务组` : groupName,
          selectedTasks: selectedJobForGroup.tasks, // 使用 selectedTasks 而不是 tasks
          taskConfigs: selectedJobForGroup.configs, // 使用 taskConfigs 而不是 configs
          createdAt: new Date().toISOString(),
          description: `从定时任务创建于 ${new Date().toLocaleString()}`
        }
        addTaskGroup(taskGroupData)
        addLog(`任务组 "${taskGroupData.name}" 创建成功`, 'success')
      }

      // 保存用户组
      if (groupType === 'user' || groupType === 'both') {
        const userGroupData = {
          name: groupType === 'both' ? `${groupName}_用户组` : groupName,
          selectedAccounts: selectedJobForGroup.accounts, // 使用 selectedAccounts 而不是 accounts
          createdAt: new Date().toISOString(),
          description: `从定时任务创建于 ${new Date().toLocaleString()}`
        }
        addUserGroup(userGroupData)
        addLog(`用户组 "${userGroupData.name}" 创建成功`, 'success')
      }

      setShowSaveGroupModal(false)
      setSelectedJobForGroup(null)
      setGroupName('')
    } catch (error) {
      addLog(`保存组失败: ${error.message}`, 'error')
    }
  }

  // 加载定时任务配置到编辑界面
  const handleLoadJobConfig = (job) => {
    if (onLoadJobConfig) {
      console.log('加载定时任务配置:', {
        tasks: job.tasks,
        accounts: job.accounts,
        configs: job.configs,
        requestInterval: job.requestInterval
      })
      onLoadJobConfig({
        tasks: job.tasks,
        accounts: job.accounts,
        configs: job.configs,
        requestInterval: job.requestInterval
      })
      addLog(`已加载定时任务 ${job.id} 的配置: ${job.tasks.length}个任务, ${job.accounts.length}个账号`, 'success')
    }
  }

  return (
    <div>
      {/* 执行模式选择 */}
      <Form.Group className="mb-3">
        <Form.Label>执行模式</Form.Label>
        <div>
          <Form.Check
            type="radio"
            id="mode-once"
            name="executionMode"
            label="执行一次"
            checked={executionMode === 'once'}
            onChange={() => onExecutionModeChange('once')}
          />
          <Form.Check
            type="radio"
            id="mode-scheduled"
            name="executionMode"
            label="每天定时执行"
            checked={executionMode === 'scheduled'}
            onChange={() => onExecutionModeChange('scheduled')}
          />
        </div>
      </Form.Group>

      {/* 定时设置 */}
      {executionMode === 'scheduled' && (
        <Form.Group className="mb-3">
          <Form.Label>执行时间</Form.Label>
          <Form.Control
            type="time"
            value={scheduleTime}
            onChange={(e) => onScheduleTimeChange(e.target.value)}
          />
          <Form.Text className="text-muted">
            每天在指定时间自动执行选中的任务
          </Form.Text>
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <Form.Label>请求间隔（毫秒）</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="50"
          value={requestInterval}
          onChange={(e) => onRequestIntervalChange?.(e.target.value)}
          onBlur={(e) => onRequestIntervalChange?.(normalizeRequestInterval(e.target.value))}
          disabled={isExecuting}
        />
        <Form.Text className="text-muted">
          控制日常任务接口之间的最小间隔，默认 350ms。
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="activity-skip-switch"
          label="活跃度已完成则跳过"
          checked={activitySkipEnabled !== false}
          onChange={(e) => onActivitySkipEnabledChange?.(e.target.checked)}
          disabled={isExecuting}
        />
        <Form.Text className="text-muted">
          执行前检查活跃度弹窗接口，对应活跃度任务已完成（info[key]=2）时跳过该任务，不执行其活动内容。
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="daily-runtime-reset-switch"
          label="每日凌晨重置运行状态"
          checked={dailyRuntimeResetEnabled !== false}
          onChange={(e) => onDailyRuntimeResetEnabledChange?.(e.target.checked)}
          disabled={isExecuting}
        />
        <Form.Text className="text-muted">
          默认开启。00:00 会中止旧日请求、清空队列和延迟重试，再按当天计划等待下次执行；账号、配置和长期学习数据不会清除。
        </Form.Text>
      </Form.Group>

      {/* 执行按钮 */}
      <div className="d-grid mb-3">
        <Button
          variant="primary"
          size="lg"
          onClick={handleExecuteClick}
          disabled={!canExecute}
        >
          {isExecuting ? '执行中...' : 
           executionMode === 'once' ? '立即执行' : '设置定时执行'}
        </Button>
      </div>

      {selectedTasks.includes('sleeve_dispatch') && selectedAccounts.length > 0 && (
        <div className="d-grid mb-3">
          <Button variant="outline-secondary" onClick={() => {
            selectedAccounts.forEach(accountKey => taskManager?.stopTask('sleeve_dispatch', accountKey))
            addLog('已停止所选账号的锦囊循环；如有每日定时计划，请在下方单独取消', 'info')
          }}>
            停止所选账号锦囊循环
          </Button>
        </div>
      )}

      {/* 执行进度 */}
      {isExecuting && (
        <div className="execution-progress mb-3">
          <div className="d-flex justify-content-between mb-1">
            <small>执行进度</small>
            <small>{Math.round(executionProgress)}%</small>
          </div>
          <ProgressBar now={executionProgress} />
          {currentTask && (
            <small className="text-muted">当前任务: {currentTask}</small>
          )}
          {currentAccount && (
            <small className="text-muted d-block">当前账号: {currentAccount}</small>
          )}
        </div>
      )}

      {/* 执行前检查 */}
      {!canExecute && (
        <Alert variant="warning" className="mb-3">
          {selectedTasks.length === 0 && '请选择要执行的任务'}
          {selectedAccounts.length === 0 && '请选择要执行的账号'}
          {isExecuting && '任务执行中，请等待完成'}
        </Alert>
      )}

      {/* 定时任务列表 */}
      {effectiveScheduledJobsList.length > 0 && (
        <div className="mt-3">
          <h6>定时任务</h6>
          {effectiveScheduledJobsList.map(job => (
            <div key={job.id} className="scheduled-job-item border rounded p-2 mb-2">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="fw-bold text-primary">
                    {new Date(job.scheduledTime).toLocaleString()}
                  </div>
                  <div className="small text-muted mb-1">
                    {job.tasks.length}个任务 • {job.accounts.length}个账号
                    {job.requestInterval !== undefined && (
                      <span> • 请求间隔: {job.requestInterval}ms</span>
                    )}
                    {job.lastExecutedAt && (
                      <span> • 上次执行: {new Date(job.lastExecutedAt).toLocaleString()}</span>
                    )}
                  </div>
                  
                  {/* 显示任务列表 */}
                  <div className="small">
                    <strong>任务:</strong>
                    <div className="ms-2 mt-1">
                      <HorizontalTagList
                        items={job.tasks}
                        maxVisible={5}
                        renderItem={(taskId) => {
                          const task = taskManager?.getTask(taskId)
                          const hasConfig = job.configs[taskId] && Object.keys(job.configs[taskId]).length > 0
                          return (
                            <span key={taskId} className="badge bg-primary me-1 mb-1 d-inline-flex align-items-center">
                              <span className="me-1">{task?.icon || '📋'}</span>
                              <span>{task?.name || taskId}</span>
                              {hasConfig && (
                                <span className="badge bg-light text-dark ms-1" style={{ fontSize: '0.6rem' }}>✓</span>
                              )}
                            </span>
                          )
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* 显示账号列表 */}
                  <div className="small mt-1">
                    <strong>账号:</strong>
                    <div className="ms-2 mt-1">
                      <HorizontalTagList
                        items={job.accounts}
                        maxVisible={5}
                        renderItem={(accountKey) => (
                          <span key={accountKey} className="badge bg-info me-1 mb-1">
                            {accountKey}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="text-end">
                  <div className="mb-2">
                    <span className={`badge bg-${
                      job.status === 'scheduled' ? 'primary' :
                      job.status === 'executing' ? 'warning' : 
                      job.status === 'expired' ? 'secondary' : 'danger'
                    }`}>
                      {job.status === 'scheduled' ? '已安排' :
                       job.status === 'executing' ? '执行中' : 
                       job.status === 'expired' ? '已过期' : '失败'}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleLoadJobConfig(job)}
                      disabled={job.status === 'executing'}
                      title="加载配置到编辑界面"
                    >
                      📝 编辑
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleSaveAsGroup(job)}
                      disabled={job.status === 'executing'}
                      title="保存为任务组/用户组"
                    >
                      💾 存为组
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => cancelScheduledJob(job.id)}
                      disabled={job.status === 'executing'}
                      title="取消定时任务"
                    >
                      ❌ 取消
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* 配置快照提示 */}
              <div className="mt-2 pt-2 border-top">
                <small className="text-success">
                  ✓ 此定时任务使用创建时的配置快照，不会受到后续配置修改影响
                </small>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 确认模态框 */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>确认设置定时任务</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>将在每天 <strong>{scheduleTime}</strong> 自动执行以下任务：</p>
          
          <div className="row">
            <div className="col-md-6">
              <h6>任务列表 ({selectedTasks.length} 个)</h6>
              <div className="border rounded p-2 mb-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                {selectedTasks.map(taskId => {
                  const task = taskManager?.getTask(taskId)
                  const hasConfig = taskConfigs[taskId] && Object.keys(taskConfigs[taskId]).length > 0
                  return (
                    <div key={taskId} className="d-flex align-items-center mb-1">
                      <span className="me-2">{task?.icon || '📋'}</span>
                      <span className="flex-grow-1">{task?.name || taskId}</span>
                      {hasConfig && (
                        <span className="badge bg-success">已配置</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
            
            <div className="col-md-6">
              <h6>账号列表 ({selectedAccounts.length} 个)</h6>
              <div className="border rounded p-2 mb-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                {selectedAccounts.map(accountKey => (
                  <div key={accountKey} className="mb-1">
                    <span className="badge bg-info">{accountKey}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <Alert variant="success">
            <strong>配置快照保护</strong><br/>
            定时任务将使用当前的配置状态创建快照，即使您之后修改了任务配置，也不会影响已设置的定时任务。
          </Alert>
          
          <Alert variant="info">
            定时任务将在后台运行，您可以随时在此页面查看和取消。每个定时任务都是独立的，拥有自己的配置副本。
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={scheduleExecution}>
            确认设置
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 保存为组模态框 */}
      <Modal show={showSaveGroupModal} onHide={() => setShowSaveGroupModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>保存为组</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>组名称</Form.Label>
            <Form.Control
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="输入组名称"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>保存类型</Form.Label>
            <Form.Check
              type="radio"
              id="save-both"
              name="groupType"
              label="保存任务组和用户组"
              checked={groupType === 'both'}
              onChange={() => setGroupType('both')}
            />
            <Form.Check
              type="radio"
              id="save-task"
              name="groupType"
              label="仅保存任务组"
              checked={groupType === 'task'}
              onChange={() => setGroupType('task')}
            />
            <Form.Check
              type="radio"
              id="save-user"
              name="groupType"
              label="仅保存用户组"
              checked={groupType === 'user'}
              onChange={() => setGroupType('user')}
            />
          </Form.Group>

          {selectedJobForGroup && (
            <Alert variant="info">
              <div className="small">
                <strong>将保存以下内容：</strong>
                {(groupType === 'task' || groupType === 'both') && (
                  <div>• 任务组: {selectedJobForGroup.tasks.length} 个任务及其配置</div>
                )}
                {(groupType === 'user' || groupType === 'both') && (
                  <div>• 用户组: {selectedJobForGroup.accounts.length} 个账号</div>
                )}
              </div>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveGroupModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={confirmSaveAsGroup}>
            确认保存
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ExecutionController
